import Head from 'next/head';
import { useEffect, useState } from 'react';
import { server } from '../../config';
import { IProduct } from '../../model/product';
import SimilarProducts from '../similar-products/similar-products';
import CartHandler from './../../utils/shopping-cart-handler';
import styles from './product.module.css';
import CheckedBox from '../checked-box/checked-box';
import Link from 'next/link';
export default function Product({product}: {product: IProduct}) {
	const [alternatives, setAlternatvies] = useState<IProduct[]>([]);
	const [fetched, setFetched] = useState<boolean>(false);
	const [bestAlternative, setBestAlternative] = useState<IProduct | null>(product);
	const [similarProducts, setSimilarProducts] = useState<IProduct[]>([]);
	const [jsonLDProductInfo, setLDProductInfo] = useState<any>({});
	const [productIsInCart, setProductIsInCart] = useState<boolean>(false);
	const [showCheckedBox, setShowCheckedBox] = useState<boolean>(false);
	const [productNotFound, setProductNotFound] = useState<boolean>(false);
	useEffect(() => {	

		findSimilarProducts().then();
		if (product && !product?.gtin?.length) {
			setBestAlternative(product);
			setLDProductInfo(generateJsonLDProductInfo(product));
			return;
		}
		if (!product && fetched) {
			setProductNotFound(true);
		}

		fetch(`${server}/api/product/?gtin=${product?.gtin}`).then(async response => {
			let result = await response.json();
			setFetched(true);
			if (result.length && product?.gtin?.length) {
				const lowestPrice: IProduct = result.reduce((prev: IProduct, current: IProduct) => ((prev?.price || 0) < (current?.price || 0)) ? prev : current);
				setBestAlternative(lowestPrice);
			} else {
				setBestAlternative(product);
			}
			const alreadyAdded: string[] = []
			const _result = result.filter((p: IProduct) => {
				if (!alreadyAdded.includes(p.affiliate)) {
					alreadyAdded.push(p.affiliate);
					return true;
				}
			});
			if (product?.gtin?.length) {
				setAlternatvies(_result);
				setLDProductInfo(generateJsonLDProductInfo(product));
			}			
		});
	}, [product]);

	useEffect(() => {
		if (bestAlternative) {
			checkIfProductIsInCart();
		}
	}, [bestAlternative])

	function checkIfProductIsInCart() {
		const cart = CartHandler.getCart();
		let isInCart = false;
		if (cart && cart.length && bestAlternative) {
			let prodIndex = cart.findIndex((p: IProduct) => p._id === bestAlternative._id);
			isInCart = prodIndex > -1;
		}
		setShowCheckedBox(isInCart);
		setProductIsInCart(isInCart);		
	}

	const generateJsonLDProductInfo = (product: IProduct) => {
		var toReturn: { [key: string]: any; } =  {
			'@context': "https://schema.org/",
			'@type': "Product",
			name: product?.name,
			image: product?.image,
			description: product?.shortDescription,
			gtin13: product?.gtin,
			brand: {
				'@type': "Thing",
				name: product?.brand
			},
			offers: {
				"@type": "Offer",
				url: `https://www.babymoon.no/produkter/${product.brand}/${encodeURI(product?.urlName)}`,				
				price: (product.price || 0).toFixed(2).toString(),
				priceCurrency: "NOK",
				availability: "https://schema.org/InStock",
				condition: "https://schema.org/NewCondition",
				priceValidUntil: '2024-12-12',
				seller: {
				"@type": "Organization",
					name: `${product.affiliate[0] + product.affiliate.substring(1).toLowerCase()}`,
					url: product.url
				}				
			},
			adult: true
		}

		if (product?.rating) {
			toReturn['aggregateRating'] = {
				'@type': 'AggregateRating',
				ratingValue: product.rating,
				reviewCount: product.rating
			}
		}

		return toReturn;
	}

	const findSimilarProducts = async () => {
		const response = await fetch(`${server}/api/product/findSimilar?q=${product?.name}`);
		const res = await response.json();
		setSimilarProducts(res);
	}
	if(productNotFound) {
		return (<div className={styles.productContainer} style={{background: 'transparent'}}>
			<p>
				<strong style={{fontSize: '18px'}}>Produktet ble ikke funnet.</strong><br/><br/>
				Det ser ut som vi ikke har produktet du leter i produktkatalogen vår.<br/>
				Hvis du bruker søkefeltet over, så tipper vi at du kan finne et godt alternativ.
			</p>
			<p>
				<Link href="/">Gå til forsiden</Link>
			</p>
		</div>);
	}

	if (!bestAlternative) {
		return (<div className={styles.productContainer}>
			<span style={{fontSize: '18px'}}><strong>Finner beste alternativ...</strong></span>
		</div>);
	}
	return (
		<div className={styles.productContainer} style={{}}>
			<Head>
				<title>{`Babymoon | ${bestAlternative?.name}`}</title>
				<meta name="description" content={bestAlternative?.shortDescription} />
				<meta name="og:title" content={`Babymoon | ${bestAlternative?.name}`}></meta>
				<meta name="og:description" content={bestAlternative?.shortDescription}></meta>
				<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLDProductInfo) }}/>
			</Head>
			<div className={styles.innerContainer}>
				<div>
					<img style={{height: '256px', marginRight: '32px'}} src={product?.image} alt={product?.name} />
				</div>
				<div style={{textAlign: 'left'}}>
					<div style={{display: 'flex', justifyContent: 'space-between'}}>
					<h1>{bestAlternative?.name}</h1>
					{showCheckedBox && <CheckedBox/>}
					</div>
					<div style={{fontSize: '10px', color: 'gray', marginBottom: '24px', marginTop: '-16px'}}>
						{bestAlternative.gtin?.length ? `Artikkelnr. ${bestAlternative.gtin}` : <></>}<br/>
						{bestAlternative.brand?.length ? `Merke: ${bestAlternative.brand}` : <></>}
					</div>
					<article>
						<strong>Produktbeskrivelse</strong><br/>
						<p dangerouslySetInnerHTML={{__html: bestAlternative?.longDescription || 'Ingen beskrivelse fra leverandøren.'}}></p>
					</article>
					{bestAlternative.affiliate === 'NYTELSE' && 
						<div style={{fontSize: '.8em', fontWeight: 600}}>
							Rabattkode tilgjengelig:<br/>
							Husk å benytte vår eksklusive rabattkode <strong><u>BSL24</u></strong> i kassen hos Nytelse og å få 15% rabatt.
						</div>
					}
					{bestAlternative.affiliate === 'MERLYKKE' && 
						<div style={{fontSize: '.8em', fontWeight: 600}}>
							Rabattkode tilgjengelig:<br/>
							Husk å benytte vår eksklusive rabattkode <strong><u>BSL2024</u></strong> i kassen hos Merlykke og å få 15% rabatt.
						</div>
					}										
					{alternatives.length > 1 ? 
					<section>
						<strong>Tilgjengelig hos</strong><br/>
						<p style={{fontSize: '.9em'}}>Denne artikkelen er tilgjengelig hos flere leverandører.<br/>
						Prisene kan variere og være avhengige av kampanjer, tilbud, rabatter ol.</p>
						<table className={styles.priceCompareTable}>
							<tbody>
								<tr>
									{alternatives.map((alt, i) => {return <th key={`th_alt_${i}`}>{alt.affiliate}</th>})}
								</tr>
								<tr>
									{alternatives.map((alt, i) => {return <td  key={`td_alt_${i}`}><a href={alt.url} rel="nofollow" target="_blank">{alt.price},-</a></td>})}
								</tr>		
							</tbody>		
						</table>
					</section>
					: <></>}					
					<br/>
					<div className={styles.buyBtnWrapper}>
						<a href={bestAlternative?.url} title={`Kjøp produktet hos ${bestAlternative.affiliate}.`} className={styles.buyBtn} rel="nofollow" target="_blank">KJØP HOS {bestAlternative.affiliate} FOR <span itemProp="price">{bestAlternative?.priceStr}</span></a>
					</div>
					<div className={styles.buyBtnWrapper} onClick={() => {
						CartHandler.toggleItem(bestAlternative);
						checkIfProductIsInCart();
						setTimeout(() => {
							if (!productIsInCart) {
								const el = document.getElementById('checked-box');
								if (el) {
									window.scrollTo(el.offsetLeft, el.offsetTop - 64);
								}
							}
						}, 10);

					}} style={{color: '#e39961',fontWeight: 500, textDecoration: 'underline', cursor: 'pointer'}}>
						{productIsInCart ? '- Fjern fra handleliste' : '+ Legg til i handleliste'}
					</div>				
				</div>
			</div>
			<div style={{marginTop: '56px'}}>
				<SimilarProducts products={similarProducts}/>
			</div>
			<div className="showInMobile" style={{ marginTop: '32px' }}>
				<a href="https://track.adtraction.com/t/t?a=1870260139&as=1907976616&t=2&tk=1"><img src="https://track.adtraction.com/t/t?a=1870260139&as=1907976616&t=1&tk=1&i=1" width="300" height="250" /></a>
      		</div>  
		</div>
	);
}